// Review screen for an AI-prepared journal draft (spec §8) plus result
// handling (§9). One view: header facts, totals + balance indicator, line
// table, sources, issues, last error, actions, and (full variant) the audit
// timeline. Used by the chat card (variant="compact") and the AI Drafts audit
// tab (variant="full").
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import {
	Button,
	Checkbox,
	DatePicker,
	Input,
	Modal,
	Spin,
	Switch,
	Table,
	Timeline,
	Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	approveJournalDraft,
	declineJournalDraft,
	editJournalDraft,
	getJournalDraft,
	getJournalDraftQboPayload,
	parseJournalDraftError,
	revalidateJournalDraft,
	updateJournalDraft,
} from 'redux/action/journalDraftAction';
import { toastText } from 'utils/utils';
import type {
	DraftDetail,
	JournalDraftLine,
	PostFailure,
	PostResult,
	ValidationIssue,
} from '../types';
import {
	EXPIRING_STATUSES,
	STAGE_LABEL,
	STATUS_META,
	accountsTouched,
	centsToString,
	formatMoney,
	formatTimestamp,
	formatTxnDate,
	isTerminalStatus,
	qboJournalUrl,
	toCents,
} from '../utils';
import styles from './index.module.scss';

export interface JournalDraftReviewProps {
	draftId: string;
	companyId?: string;
	variant?: 'full' | 'compact';
	// Forces read-only regardless of status (actions hidden).
	readOnly?: boolean;
	onPosted?: (result: PostResult) => void;
	onDraftChange?: (draft: DraftDetail) => void;
}

const POLL_MS = 4000;
const MAX_POLLS = 15;

const issueLines = (issue: ValidationIssue) =>
	issue.lineNos && issue.lineNos.length
		? ` (line${issue.lineNos.length > 1 ? 's' : ''} ${issue.lineNos.join(', ')})`
		: '';

export const StatusPill: FC<{ status: DraftDetail['status'] }> = ({ status }) => {
	const meta = STATUS_META[status] ?? { label: status, tone: 'neutral' };
	return (
		<span className={`${styles.pill} ${styles[`pill--${meta.tone}`]}`}>
			{meta.label}
		</span>
	);
};

const ConfidenceBadge: FC<{ confidence: JournalDraftLine['confidence'] }> = ({
	confidence,
}) => (
	<span className={`${styles.badge} ${styles[`badge--${confidence}`]}`}>
		{confidence}
	</span>
);

const JournalDraftReview: FC<JournalDraftReviewProps> = ({
	draftId,
	companyId,
	variant = 'full',
	readOnly = false,
	onPosted,
	onDraftChange,
}) => {
	const compact = variant === 'compact';
	const [draft, setDraft] = useState<DraftDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [posting, setPosting] = useState(false);
	const postingRef = useRef(false);
	const [busyAction, setBusyAction] = useState<null | 'edit' | 'save' | 'decline' | 'revalidate'>(null);
	const [postResult, setPostResult] = useState<PostResult | null>(null);
	const [postFailure, setPostFailure] = useState<PostFailure | null>(null);

	const [form, setForm] = useState({
		txnDate: '',
		memo: '',
		adjustment: false,
		docNumber: '',
	});

	const [declineOpen, setDeclineOpen] = useState(false);
	const [declineReason, setDeclineReason] = useState('');

	const [showPayload, setShowPayload] = useState(false);
	const [payload, setPayload] = useState<unknown>(null);
	const [payloadLoading, setPayloadLoading] = useState(false);
	const [payloadError, setPayloadError] = useState<string | null>(null);

	const [showHistory, setShowHistory] = useState(!compact);
	const pollCount = useRef(0);
	const [pollStalled, setPollStalled] = useState(false);
	// Kept in a ref so an inline callback from the parent doesn't re-create
	// load/applyDraft (and restart the poll timer) on every parent render.
	const onDraftChangeRef = useRef(onDraftChange);
	onDraftChangeRef.current = onDraftChange;

	const applyDraft = useCallback(
		(next: DraftDetail | undefined | null) => {
			if (!next || !next.id) {
				return;
			}
			setDraft(next);
			if (next.status === 'POSTED') {
				// A post that timed out client-side can still land; once the draft
				// says POSTED, an earlier "Not posted" note is stale.
				setPostFailure(null);
			}
			setForm({
				txnDate: next.txnDate || '',
				memo: next.memo || '',
				adjustment: !!next.adjustment,
				docNumber: next.docNumber || '',
			});
			// Any change to the draft invalidates a previously fetched payload.
			setPayload(null);
			setShowPayload(false);
			onDraftChangeRef.current?.(next);
		},
		[]
	);

	const load = useCallback(
		async (silent = false) => {
			if (!silent) {
				setLoading(true);
			}
			try {
				applyDraft(await getJournalDraft(draftId, companyId));
				setLoadError(null);
			} catch (err: any) {
				setLoadError(
					err?.response?.data?.message || "Couldn't load this journal draft."
				);
			} finally {
				if (!silent) {
					setLoading(false);
				}
			}
		},
		[draftId, companyId, applyDraft]
	);

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftId, companyId]);

	// A draft reopened mid-post (APPROVED / POSTING_TO_QBO) settles on its own;
	// poll briefly so the screen catches the outcome.
	useEffect(() => {
		if (!draft || posting) {
			return;
		}
		if (draft.status !== 'APPROVED' && draft.status !== 'POSTING_TO_QBO') {
			pollCount.current = 0;
			setPollStalled(false);
			return;
		}
		if (pollCount.current >= MAX_POLLS) {
			setPollStalled(true);
			return;
		}
		const timer = setTimeout(() => {
			pollCount.current += 1;
			load(true);
		}, POLL_MS);
		return () => clearTimeout(timer);
	}, [draft, posting, load]);

	if (loading && !draft) {
		return (
			<div className={styles.loading}>
				<Spin indicator={<LoadingOutlined spin />} /> Loading journal draft…
			</div>
		);
	}

	if (!draft) {
		return (
			<div className={`${styles.panel} ${styles['panel--danger']}`}>
				{loadError || "Couldn't load this journal draft."}{' '}
				<Button size="small" onClick={() => load()}>
					Try again
				</Button>
			</div>
		);
	}

	const blocking = draft.validation?.blocking ?? [];
	const warnings = draft.validation?.warnings ?? [];
	const blockingLineNos = new Set(blocking.flatMap((b) => b.lineNos ?? []));
	const terminal = isTerminalStatus(draft.status);
	const interactive = !readOnly && !terminal;
	const qboWritable = !!draft.qboConnection?.connected && !!draft.qboConnection?.writable;
	const canShowPost =
		interactive &&
		draft.permissions?.canApprove === true &&
		draft.status === 'IN_REVIEW';
	const editing =
		interactive && draft.status === 'NEEDS_CLARIFICATION' && draft.permissions?.canEdit;
	const openClarifications = (draft.clarifications ?? []).filter((c) => !c.resolved);
	const diffCents = toCents(draft.totalDebit) - toCents(draft.totalCredit);

	// ── actions ────────────────────────────────────────────────────────────
	const handlePost = async () => {
		if (postingRef.current) {
			return; // double-submit guard (state updates are async)
		}
		postingRef.current = true;
		setPosting(true);
		setPostFailure(null);
		try {
			const result = await approveJournalDraft(
				draft.id,
				{ version: draft.version, linesHash: draft.linesHash },
				companyId
			);
			setPostResult(result);
			if (result?.draft) {
				applyDraft(result.draft);
			} else {
				load(true);
			}
			onPosted?.(result);
		} catch (err) {
			const failure = parseJournalDraftError(err);
			setPostFailure(failure);
			if (failure.draft) {
				applyDraft(failure.draft);
			} else {
				load(true);
			}
		} finally {
			postingRef.current = false;
			setPosting(false);
		}
	};

	const runAction = async (
		kind: 'edit' | 'save' | 'decline' | 'revalidate',
		fn: () => Promise<DraftDetail | undefined>,
		successText?: string
	) => {
		if (busyAction) {
			return false;
		}
		setBusyAction(kind);
		try {
			const next = await fn();
			if (next && next.id) {
				applyDraft(next);
			} else {
				await load(true);
			}
			if (successText) {
				toastText(successText, 'success');
			}
			return true;
		} catch (err) {
			toastText(parseJournalDraftError(err).message, 'error');
			return false;
		} finally {
			setBusyAction(null);
		}
	};

	const handleEdit = () => {
		setPostFailure(null);
		runAction('edit', () => editJournalDraft(draft.id, companyId));
	};

	const handleSave = () => {
		setPostFailure(null);
		if (!form.txnDate) {
			toastText('Pick a journal date.', 'error');
			return;
		}
		runAction(
			'save',
			() =>
				updateJournalDraft(
					draft.id,
					{
						txnDate: form.txnDate,
						memo: form.memo,
						adjustment: form.adjustment,
						docNumber: form.docNumber.trim() ? form.docNumber.trim() : null,
					},
					companyId
				),
			'Draft updated and re-validated.'
		);
	};

	const handleDecline = async () => {
		const reason = declineReason.trim();
		if (reason.length < 3) {
			return;
		}
		const ok = await runAction(
			'decline',
			() => declineJournalDraft(draft.id, reason, companyId),
			'Draft declined.'
		);
		if (ok) {
			setDeclineOpen(false);
			setDeclineReason('');
		}
	};

	const handleRevalidate = () => {
		setPostFailure(null);
		runAction(
			'revalidate',
			() => revalidateJournalDraft(draft.id, companyId),
			'Draft rebuilt from the current Cost Allocation.'
		);
	};

	const togglePayload = async (checked: boolean) => {
		setShowPayload(checked);
		if (!checked || payload !== null) {
			return;
		}
		setPayloadLoading(true);
		setPayloadError(null);
		try {
			setPayload(await getJournalDraftQboPayload(draft.id, companyId));
		} catch (err) {
			setPayloadError(parseJournalDraftError(err).message);
		} finally {
			setPayloadLoading(false);
		}
	};

	// ── sections ───────────────────────────────────────────────────────────
	const renderSuccess = () => {
		// Fresh PostResult wins; otherwise rebuild the same panel from a POSTED draft.
		const r = postResult;
		if (!r && draft.status !== 'POSTED') {
			return null;
		}
		const companyName = r?.companyName ?? draft.companyName;
		const txnDate = r?.txnDate ?? draft.txnDate;
		const total = r?.total ?? draft.totalDebit;
		const accounts = r?.accountsTouched ?? accountsTouched(draft);
		const docNumber = r?.qboDocNumber ?? draft.qboDocNumber;
		const txnId = r?.qboTxnId ?? draft.qboTxnId;
		const url = r?.qboUrl || qboJournalUrl(txnId);
		const approver = r?.approvedBy?.name ?? draft.approvedBy?.name;
		const onRetry = r ? r.postedOnRetry : !!draft.postedOnRetry;
		return (
			<div className={`${styles.panel} ${styles['panel--success']}`} role="status">
				<div className={styles['panel__title']}>✓ Posted to QuickBooks</div>
				<dl className={styles.facts}>
					<dt>Company</dt>
					<dd>{companyName}</dd>
					<dt>Date</dt>
					<dd>{formatTxnDate(txnDate)}</dd>
					<dt>Total</dt>
					<dd>{formatMoney(total)}</dd>
					<dt>Accounts</dt>
					<dd>{accounts.join(', ')}</dd>
					<dt>QuickBooks</dt>
					<dd>
						{docNumber ? `Journal #${docNumber}` : 'Journal'}
						{txnId ? ` (ID ${txnId})` : ''}
						{url && (
							<>
								{' · '}
								<a href={url} target="_blank" rel="noreferrer">
									Open in QuickBooks
								</a>
							</>
						)}
					</dd>
					<dt>Approved by</dt>
					<dd>
						{approver || '—'}
						{draft.approvedAt ? ` on ${formatTimestamp(draft.approvedAt)}` : ''}
					</dd>
				</dl>
				{onRetry && (
					<div className={styles['panel__note']}>Posted on retry, same approval.</div>
				)}
			</div>
		);
	};

	const renderFailure = () => {
		if (draft.status === 'POSTED') {
			return null;
		}
		if (postFailure) {
			return (
				<div className={`${styles.panel} ${styles['panel--danger']}`} role="alert">
					<div className={styles['panel__title']}>
						Not posted — failed at {STAGE_LABEL[postFailure.stage] ?? postFailure.stage}
					</div>
					<div className={styles['panel__error']}>{postFailure.message}</div>
					{postFailure.whatToChange && (
						<div>
							<strong>What to change:</strong> {postFailure.whatToChange}
						</div>
					)}
					<div className={styles['panel__note']}>
						{postFailure.needsReapproval
							? 'Retrying needs a fresh approval — review the entry and click Post again.'
							: 'This can be retried with the same approval.'}
					</div>
				</div>
			);
		}
		const e = draft.lastError;
		if (!e) {
			return null;
		}
		const needsReapproval =
			typeof e.needsReapproval === 'boolean'
				? e.needsReapproval
				: draft.status !== 'QBO_POSTING_FAILED';
		return (
			<div className={`${styles.panel} ${styles['panel--danger']}`}>
				<div className={styles['panel__title']}>
					Last attempt failed at {STAGE_LABEL[e.stage] ?? e.stage}
					{e.at ? ` · ${formatTimestamp(e.at)}` : ''}
				</div>
				<div className={styles['panel__error']}>
					{e.code ? `[${e.code}] ` : ''}
					{e.message}
				</div>
				{e.whatToChange && (
					<div>
						<strong>What to change:</strong> {e.whatToChange}
					</div>
				)}
				<div className={styles['panel__note']}>
					{draft.status === 'QBO_POSTING_FAILED'
						? "This draft can't be retried. Start a new journal from the chat or Cost Allocations."
						: needsReapproval
							? 'Re-approval is required before it is posted again.'
							: 'No re-approval needed for a retry.'}
				</div>
			</div>
		);
	};

	const lineColumns = [
		{
			title: '#',
			dataIndex: 'lineNo',
			key: 'lineNo',
			width: 40,
		},
		{
			title: 'Account',
			key: 'account',
			render: (_: unknown, line: JournalDraftLine) => (
				<span className={styles.account}>
					{line.accountName || <em>Unmapped</em>}
					{line.source !== 'stated' && (
						<Tooltip
							title={
								<span style={{ fontSize: 12 }}>
									<strong>Why this account ({line.source}):</strong>{' '}
									{line.why || 'No explanation was recorded for this line.'}
								</span>
							}
						>
							<InfoCircleOutlined
								className={styles['why-icon']}
								aria-label="Why this account"
							/>
						</Tooltip>
					)}
					{line.ruleName && <span className={styles.rule}>{line.ruleName}</span>}
				</span>
			),
		},
		{
			title: 'Debit',
			key: 'debit',
			align: 'right' as const,
			render: (_: unknown, line: JournalDraftLine) =>
				line.postingType === 'Debit' ? formatMoney(line.amount) : '',
		},
		{
			title: 'Credit',
			key: 'credit',
			align: 'right' as const,
			render: (_: unknown, line: JournalDraftLine) =>
				line.postingType === 'Credit' ? formatMoney(line.amount) : '',
		},
		{
			title: 'Class / Location',
			key: 'classLocation',
			render: (_: unknown, line: JournalDraftLine) =>
				[line.className, line.departmentName].filter(Boolean).join(' / ') || '—',
		},
		{
			title: 'Description',
			key: 'description',
			render: (_: unknown, line: JournalDraftLine) => {
				const secondary = [line.employeeName, line.customerName]
					.filter(Boolean)
					.join(' · ');
				return (
					<>
						<div>{line.description || '—'}</div>
						{secondary && <div className={styles.muted}>{secondary}</div>}
					</>
				);
			},
		},
		{
			title: 'Confidence',
			key: 'confidence',
			render: (_: unknown, line: JournalDraftLine) => (
				<ConfidenceBadge confidence={line.confidence} />
			),
		},
	];

	const sources = draft.sourceDocuments ?? [];
	const expiresSoon =
		EXPIRING_STATUSES.includes(draft.status) && draft.expiresAt
			? formatTimestamp(draft.expiresAt)
			: null;

	return (
		<div className={`${styles.review} ${compact ? styles['review--compact'] : ''}`}>
			{/* Header: company, status pill, expiry */}
			<div className={styles.header}>
				<div>
					<div className={styles['header__company']}>{draft.companyName}</div>
					<div className={styles.muted}>
						AI payroll journal{draft.payPeriodLabel ? ` · ${draft.payPeriodLabel}` : ''} · v
						{draft.version}
					</div>
				</div>
				<div className={styles['header__status']}>
					<StatusPill status={draft.status} />
					{expiresSoon && <span className={styles.muted}>Expires {expiresSoon}</span>}
				</div>
			</div>

			{/* Facts + totals + balance indicator */}
			<div className={styles.summary}>
				<dl className={styles.facts}>
					<dt>Date</dt>
					<dd>{formatTxnDate(draft.txnDate)}</dd>
					<dt>Memo</dt>
					<dd>{draft.memo || <em className={styles.muted}>No memo</em>}</dd>
					{draft.adjustment && (
						<>
							<dt>Adjustment</dt>
							<dd>Yes (adjusting entry)</dd>
						</>
					)}
					{draft.docNumber && (
						<>
							<dt>Reference #</dt>
							<dd>{draft.docNumber}</dd>
						</>
					)}
				</dl>
				<div className={styles.totals}>
					<div>
						<span className={styles.muted}>Total debits</span>
						<strong>{formatMoney(draft.totalDebit)}</strong>
					</div>
					<div>
						<span className={styles.muted}>Total credits</span>
						<strong>{formatMoney(draft.totalCredit)}</strong>
					</div>
					{draft.balanced ? (
						<span className={`${styles.balance} ${styles['balance--ok']}`}>Balanced ✓</span>
					) : (
						<span className={`${styles.balance} ${styles['balance--bad']}`}>
							Not balanced
							{diffCents !== 0 ? ` (off by ${formatMoney(centsToString(Math.abs(diffCents)))})` : ''}
						</span>
					)}
				</div>
			</div>

			{renderSuccess()}
			{renderFailure()}

			{draft.status === 'DECLINED_BY_USER' && draft.declineReason && (
				<div className={`${styles.panel} ${styles['panel--neutral']}`}>
					<strong>Declined:</strong> {draft.declineReason}
				</div>
			)}

			{interactive && draft.qboConnection && !qboWritable && (
				<div className={`${styles.panel} ${styles['panel--warning']}`}>
					{draft.qboConnection.message ||
						(!draft.qboConnection.connected
							? 'QuickBooks is not connected for this company, so this entry can’t be posted. Reconnect in Settings → Integrations.'
							: 'The QuickBooks connection is read-only, so this entry can’t be posted. Reconnect with write access in Settings → Integrations.')}
				</div>
			)}

			{blocking.length > 0 && (
				<div className={`${styles.panel} ${styles['panel--danger']}`}>
					<div className={styles['panel__title']}>
						{blocking.length === 1 ? 'One problem blocks posting' : `${blocking.length} problems block posting`}
					</div>
					<ul className={styles.issues}>
						{blocking.map((issue, i) => (
							<li key={`b-${issue.code}-${i}`}>
								{issue.message}
								{issueLines(issue)}
							</li>
						))}
					</ul>
				</div>
			)}

			{warnings.length > 0 && (
				<div className={`${styles.panel} ${styles['panel--warning']}`}>
					<div className={styles['panel__title']}>Worth a look (won’t block posting)</div>
					<ul className={styles.issues}>
						{warnings.map((issue, i) => (
							<li key={`w-${issue.code}-${i}`}>
								{issue.message}
								{issueLines(issue)}
							</li>
						))}
					</ul>
				</div>
			)}

			{openClarifications.length > 0 && (
				<div className={`${styles.panel} ${styles['panel--warning']}`}>
					<div className={styles['panel__title']}>Open questions</div>
					<ul className={styles.issues}>
						{openClarifications.map((c) => (
							<li key={c.id}>
								{c.question}
								{c.options && c.options.length ? ` (${c.options.join(' / ')})` : ''}
							</li>
						))}
					</ul>
				</div>
			)}

			{editing && (
				<div className={styles.editForm}>
					<div className={styles['panel__title']}>Edit draft</div>
					<p className={styles.muted}>
						Lines come from the pay period’s Cost Allocation — change amounts or accounts there, then
						use Revalidate. Here you can change the date, memo, adjustment flag and reference number.
					</p>
					<label className={styles.field}>
						<span>Journal date</span>
						<DatePicker
							value={form.txnDate ? dayjs(form.txnDate, 'YYYY-MM-DD') : null}
							format="MM/DD/YYYY"
							allowClear={false}
							onChange={(value) =>
								setForm((f) => ({ ...f, txnDate: value ? value.format('YYYY-MM-DD') : '' }))
							}
						/>
					</label>
					<label className={styles.field}>
						<span>Memo</span>
						<Input.TextArea
							value={form.memo}
							autoSize={{ minRows: 2, maxRows: 4 }}
							maxLength={4000}
							onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
						/>
					</label>
					<label className={styles.field}>
						<span>Reference # (optional)</span>
						<Input
							value={form.docNumber}
							maxLength={21}
							placeholder="Leave blank to let QuickBooks number it"
							onChange={(e) => setForm((f) => ({ ...f, docNumber: e.target.value }))}
						/>
					</label>
					<Checkbox
						checked={form.adjustment}
						onChange={(e) => setForm((f) => ({ ...f, adjustment: e.target.checked }))}
					>
						Adjusting entry (month-end adjustment)
					</Checkbox>
					<div className={styles.actions}>
						<Button type="primary" loading={busyAction === 'save'} onClick={handleSave}>
							Save &amp; revalidate
						</Button>
					</div>
				</div>
			)}

			{/* Line table */}
			<div className={styles.tableWrap}>
				<Table
					size="small"
					rowKey={(line: JournalDraftLine) => String(line.lineNo)}
					dataSource={draft.lines}
					columns={lineColumns}
					pagination={false}
					scroll={compact ? { x: 640, y: 260 } : { x: 760 }}
					rowClassName={(line: JournalDraftLine) =>
						blockingLineNos.has(line.lineNo) ? styles['row--blocked'] : ''
					}
					summary={() => (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} />
							<Table.Summary.Cell index={1}>
								<strong>Total</strong>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={2} align="right">
								<strong>{formatMoney(draft.totalDebit)}</strong>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={3} align="right">
								<strong>{formatMoney(draft.totalCredit)}</strong>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={4} colSpan={3} />
						</Table.Summary.Row>
					)}
				/>
			</div>

			{/* Sources: Cost Allocation + documents */}
			{(draft.costAllocationLink || sources.length > 0) && (
				<div className={styles.sources}>
					<span className={styles.muted}>Source:</span>
					{draft.costAllocationLink && (
						<Link to={draft.costAllocationLink}>
							Cost Allocation{draft.payPeriodLabel ? ` (${draft.payPeriodLabel})` : ''}
						</Link>
					)}
					{sources
						.filter((s) => !(s.kind === 'cost_allocation' && s.url === draft.costAllocationLink))
						.map((s, i) =>
							s.url ? (
								s.url.startsWith('/') ? (
									<Link key={`src-${i}`} to={s.url}>
										{s.label}
									</Link>
								) : (
									<a key={`src-${i}`} href={s.url} target="_blank" rel="noreferrer">
										{s.label}
									</a>
								)
							) : (
								<span key={`src-${i}`}>{s.label}</span>
							)
						)}
				</div>
			)}

			{/* Power-user QBO payload preview (nothing to show for a draft that never got lines) */}
			{draft.lines.length > 0 && (
				<div className={styles.payloadToggle}>
					<Switch size="small" checked={showPayload} onChange={togglePayload} />
					<span>Preview in QBO format</span>
				</div>
			)}
			{showPayload && (
				<div className={styles.payload}>
					{payloadLoading ? (
						<Spin size="small" />
					) : payloadError ? (
						<span className={styles.error}>{payloadError}</span>
					) : (
						<pre aria-readonly="true">{JSON.stringify(payload, null, 2)}</pre>
					)}
				</div>
			)}

			{/* Actions */}
			{interactive && (
				<div className={styles.actions}>
					{draft.status === 'EXPIRED' ? (
						draft.permissions?.canEdit && (
							<Button
								type="primary"
								loading={busyAction === 'revalidate'}
								onClick={handleRevalidate}
							>
								Revalidate
							</Button>
						)
					) : (
						<>
							{canShowPost && (
								<Button
									type="primary"
									className={styles.post}
									loading={posting}
									disabled={posting || !!busyAction || !qboWritable || !draft.balanced || blocking.length > 0}
									title={blocking.length > 0 ? 'Fix the problems listed above before posting.' : undefined}
									onClick={handlePost}
								>
									{posting ? 'Posting…' : 'Post to QuickBooks'}
								</Button>
							)}
							{draft.status === 'IN_REVIEW' && draft.permissions?.canEdit && (
								<Button
									loading={busyAction === 'edit'}
									disabled={posting || !!busyAction}
									onClick={handleEdit}
								>
									Edit
								</Button>
							)}
							{draft.status === 'IN_REVIEW' && blocking.length > 0 && draft.permissions?.canEdit && (
								<Button
									loading={busyAction === 'revalidate'}
									disabled={posting || !!busyAction}
									onClick={handleRevalidate}
								>
									Revalidate
								</Button>
							)}
							{draft.permissions?.canDecline &&
								['IN_REVIEW', 'NEEDS_CLARIFICATION', 'DRAFT_READY'].includes(draft.status) && (
								<Button
									danger
									disabled={posting || !!busyAction}
									onClick={() => setDeclineOpen(true)}
								>
									Decline
								</Button>
							)}
						</>
					)}
				</div>
			)}
			{interactive && draft.status === 'IN_REVIEW' && !draft.permissions?.canApprove && (
				<div className={styles.muted}>
					An approver with the Journal Entries “Approve” permission must post this entry.
				</div>
			)}
			{(draft.status === 'APPROVED' || draft.status === 'POSTING_TO_QBO') && !posting && (
				<div className={styles.muted}>
					{pollStalled ? (
						<>
							Still waiting for QuickBooks to confirm this entry.{' '}
							<Button
								size="small"
								onClick={() => {
									pollCount.current = 0;
									setPollStalled(false);
									load(true);
								}}
							>
								Check again
							</Button>
						</>
					) : (
						<>
							<Spin size="small" /> Posting to QuickBooks…
						</>
					)}
				</div>
			)}

			{/* Audit timeline (§10) */}
			{draft.events && draft.events.length > 0 && (
				<div className={styles.history}>
					{compact && (
						<button
							type="button"
							className={styles.linkButton}
							onClick={() => setShowHistory((v) => !v)}
						>
							{showHistory ? 'Hide history' : `Show history (${draft.events.length})`}
						</button>
					)}
					{showHistory && (
						<>
							{!compact && <div className={styles['panel__title']}>History</div>}
							<Timeline
								items={draft.events.map((ev, i) => ({
									key: `${ev.createdAt}-${i}`,
									children: (
										<div className={styles.event}>
											<div>
												{ev.fromStatus ? (
													<>
														{STATUS_META[ev.fromStatus]?.label ?? ev.fromStatus} →{' '}
													</>
												) : null}
												<strong>{STATUS_META[ev.toStatus]?.label ?? ev.toStatus}</strong>
												<span className={styles.muted}> · v{ev.version}</span>
											</div>
											<div className={styles.muted}>
												{ev.actorName || ev.actorId} ({ev.actorType}) · {formatTimestamp(ev.createdAt)}
											</div>
											{ev.message && <div className={styles['event__msg']}>{ev.message}</div>}
										</div>
									),
								}))}
							/>
						</>
					)}
				</div>
			)}

			<Modal
				open={declineOpen}
				title="Decline this journal draft"
				okText="Decline draft"
				okButtonProps={{
					danger: true,
					disabled: declineReason.trim().length < 3,
					loading: busyAction === 'decline',
				}}
				onOk={handleDecline}
				onCancel={() => {
					setDeclineOpen(false);
					setDeclineReason('');
				}}
				destroyOnClose
			>
				<p>Nothing is posted to QuickBooks. Tell us why so it’s on the audit record.</p>
				<Input.TextArea
					autoFocus
					value={declineReason}
					onChange={(e) => setDeclineReason(e.target.value)}
					autoSize={{ minRows: 3, maxRows: 6 }}
					maxLength={1000}
					placeholder="Reason (required)"
				/>
				{declineReason.length > 0 && declineReason.trim().length < 3 && (
					<div className={styles.error}>Please give a reason (at least 3 characters).</div>
				)}
			</Modal>
		</div>
	);
};

export default JournalDraftReview;
